import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Seo from "@/components/Seo";

const PROBLEM_POINTS = [
    "No clear owner",
    "Disconnected tools",
    "Limited internal capacity",
    "No structured rollout",
    "Minimal measurable impact",
];

const COST_POINTS = [
    "Leads go cold",
    "Admin work builds up",
    "Follow-ups are missed",
    "Teams spend time on repetitive work",
    "Growth depends on hiring more people",
];

const SOLUTION_POINTS = [
    "Fractional Chief AI Officer capability",
    "AI opportunity mapping",
    "Practical system implementation",
    "Team enablement",
    "Ongoing optimisation",
];

const HELM = [
    { title: "Highlight", copy: "Identify where AI can create measurable business impact." },
    { title: "Engineer", copy: "Design simple systems that solve real operational problems." },
    { title: "Launch", copy: "Deploy systems into the business and ensure they are usable." },
    { title: "Manage", copy: "Optimise, report and expand over time." },
];

const SERVICES = [
    {
        name: "AI Opportunity Sprint",
        label: "Starting point",
        body: "A structured starting point that identifies where AI creates the greatest impact and defines a clear rollout plan.",
        cta: "Discuss the Sprint",
        to: "/ai-opportunity-sprint",
    },
    {
        name: "Fractional Chief AI Officer",
        label: "Ongoing leadership",
        body: "We take ownership of AI inside your business, from prioritisation to implementation and ongoing optimisation.",
        cta: "Book a consultation",
        to: "/contact",
    },
    {
        name: "Fractional AI Team",
        label: "Expanded delivery",
        body: "A broader delivery model combining leadership, implementation and ongoing support for faster rollout.",
        cta: "Talk to us",
        to: "/contact",
    },
];

const WHO_POINTS = [
    "10 to 100 employees",
    "Operational or admin-heavy environments",
    "Repetitive workflows",
    "Leadership ready to act",
];

export default function Home() {
    return (
        <div data-testid="page-home">
            <Seo
                title="Chief AI Partners | Fractional AI Leadership & Execution"
                description="Chief AI Partners takes ownership of AI inside your business. Fractional AI leadership for mid-market organisations across Australia, New Zealand and the Pacific."
                path="/"
            />

            {/* HERO */}
            <section className="cap-section pt-24 md:pt-32 pb-16" data-testid="home-hero">
                <div className="cap-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
                    <div className="lg:col-span-8">
                        <div className="cap-eyebrow mb-6">Chief AI Partners</div>
                        <h1 className="font-serif text-6xl md:text-7xl lg:text-8xl text-navy max-w-[16ch] leading-[0.98]">
                            We take ownership of AI inside your business.
                        </h1>
                    </div>
                    <div className="lg:col-span-4 lg:pb-4">
                        <p className="text-slate text-lg md:text-xl leading-relaxed">
                            We identify where AI creates impact, implement practical systems, and take responsibility for making them work.
                        </p>
                        <div className="mt-8 flex flex-col sm:flex-row gap-3">
                            <Link to="/contact" className="cap-btn-primary" data-testid="hero-primary-cta">
                                Book a 15-minute conversation
                                <ArrowRight size={16} />
                            </Link>
                            <Link to="/helm-framework" className="cap-btn-secondary" data-testid="hero-secondary-cta">
                                Explore the Helm Framework
                            </Link>
                        </div>
                    </div>
                </div>

                <div className="cap-container mt-16">
                    <div className="cap-rule" />
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 py-5 text-sm text-slate">
                        <div>
                            Based in Sydney. Supporting organisations across Australia, New Zealand and the Pacific.
                        </div>
                        <div className="text-xs uppercase tracking-[0.22em] text-teal">
                            Ownership, not advisory
                        </div>
                    </div>
                    <div className="cap-rule" />
                </div>
            </section>

            {/* CORE STATEMENT */}
            <section className="py-28 md:py-36 border-y border-sand bg-offwhite" data-testid="home-core-statement">
                <div className="cap-container">
                    <p className="font-serif text-navy text-4xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-[22ch]">
                        AI is not a tool problem. It is an ownership problem.
                    </p>
                </div>
            </section>

            {/* PROBLEM */}
            <section className="cap-section" data-testid="home-problem">
                <div className="cap-container grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="cap-eyebrow mb-4">The Real Problem</div>
                        <h2 className="font-serif text-4xl md:text-5xl text-navy leading-[1.05] tracking-tight">
                            Most businesses do not have an AI problem. They have an ownership problem.
                        </h2>
                    </div>
                    <div className="lg:col-span-7 space-y-5 text-slate text-lg leading-relaxed">
                        <p>
                            AI tools are everywhere, but most organisations still struggle to turn interest into real operational improvement.
                        </p>
                        <p>
                            Teams experiment. A few tools get tested. Some processes change. Then momentum fades.
                        </p>
                        <p className="text-navy font-medium">
                            The issue is not technology. The issue is that no one owns AI end to end.
                        </p>
                    </div>
                </div>

                <div className="cap-container mt-16">
                    <ul className="divide-y divide-sand border-y border-sand">
                        {PROBLEM_POINTS.map((point, i) => (
                            <li
                                key={point}
                                className="grid grid-cols-12 gap-6 py-6 group hover:bg-sand/25 transition-colors"
                                data-testid={`problem-point-${i}`}
                            >
                                <div className="col-span-2 md:col-span-1 text-xs uppercase tracking-[0.2em] text-teal font-semibold pt-2">
                                    {String(i + 1).padStart(2, "0")}
                                </div>
                                <div className="col-span-10 md:col-span-11 font-serif text-navy text-3xl md:text-4xl leading-tight">
                                    {point}
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* COST */}
            <section className="cap-section bg-navy text-offwhite" data-testid="home-cost">
                <div className="cap-container grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-6">
                        <div className="cap-eyebrow mb-4" style={{ color: "#2EA6A1" }}>
                            The Drag
                        </div>
                        <h2 className="font-serif text-offwhite text-4xl md:text-5xl leading-[1.05] tracking-tight">
                            The cost is already inside the business.
                        </h2>
                        <p className="mt-6 text-offwhite/80 text-lg leading-relaxed max-w-xl">
                            Every delayed response, repeated admin task and missed follow-up creates operational drag. It does not show up as a single cost, but it slows growth, reduces conversion and stretches teams.
                        </p>
                    </div>
                    <div className="lg:col-span-6">
                        <ul className="divide-y divide-offwhite/15 border-y border-offwhite/15">
                            {COST_POINTS.map((point, i) => (
                                <li
                                    key={point}
                                    className="flex items-start gap-5 py-5"
                                    data-testid={`cost-point-${i}`}
                                >
                                    <div className="cap-numeral text-teal text-3xl min-w-[3rem]">
                                        {String(i + 1).padStart(2, "0")}
                                    </div>
                                    <div className="font-serif text-offwhite text-2xl leading-snug">
                                        {point}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* SOLUTION */}
            <section className="cap-section" data-testid="home-solution">
                <div className="cap-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-5 lg:sticky lg:top-28">
                        <div className="cap-eyebrow mb-4">Our Role</div>
                        <h2 className="font-serif text-4xl md:text-5xl text-navy leading-[1.05] tracking-tight">
                            We are your fractional AI leadership team.
                        </h2>
                    </div>
                    <div className="lg:col-span-7 space-y-5">
                        <p className="text-slate text-lg leading-relaxed">
                            Chief AI Partners takes ownership of AI inside your business.
                        </p>
                        <p className="text-slate text-lg leading-relaxed">
                            We are responsible for identifying where it creates impact, implementing systems that remove repetitive work, and ensuring they are adopted across the team.
                        </p>
                        <p className="text-navy text-lg font-medium">
                            This is not advisory. This is execution.
                        </p>
                        <ul className="mt-6 space-y-3">
                            {SOLUTION_POINTS.map((p, i) => (
                                <li
                                    key={p}
                                    className="flex items-start gap-3 text-navy text-lg"
                                    data-testid={`solution-point-${i}`}
                                >
                                    <Check className="mt-1 text-teal shrink-0" size={18} strokeWidth={2.2} />
                                    <span>{p}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* HELM FLOW */}
            <section className="cap-section bg-sand/40 border-y border-sand" data-testid="home-helm">
                <div className="cap-container">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
                        <div className="lg:col-span-7">
                            <div className="cap-eyebrow mb-4">The Helm Framework</div>
                            <h2 className="font-serif text-4xl md:text-5xl text-navy leading-[1.05] tracking-tight">
                                Built on the Helm Framework.
                            </h2>
                            <p className="mt-5 text-slate text-lg leading-relaxed max-w-xl">
                                The Helm Framework (Highlight, Engineer, Launch, Manage) is our proprietary system for installing and running AI inside a business.
                            </p>
                        </div>
                        <div className="lg:col-span-5 lg:text-right">
                            <Link to="/helm-framework" className="cap-link" data-testid="helm-explore-link">
                                Explore the Helm Framework <ArrowRight size={16} />
                            </Link>
                        </div>
                    </div>

                    {/* Horizontal flow */}
                    <div className="mt-14" data-testid="helm-flow-diagram">
                        <div className="hidden md:grid grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr] items-center gap-4">
                            {HELM.map((h, idx) => (
                                <HelmNodeDesktop key={h.title} idx={idx} title={h.title} copy={h.copy} last={idx === HELM.length - 1} />
                            ))}
                        </div>
                        <div className="md:hidden space-y-4">
                            {HELM.map((h, idx) => (
                                <div key={h.title} className="border-l-2 border-navy pl-5 py-2">
                                    <div className="text-xs uppercase tracking-[0.22em] text-teal font-semibold">
                                        {String(idx + 1).padStart(2, "0")} &middot; {h.title}
                                    </div>
                                    <p className="text-slate text-sm mt-2 leading-relaxed">{h.copy}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* SERVICES */}
            <section className="cap-section" data-testid="home-services">
                <div className="cap-container">
                    <div className="max-w-3xl">
                        <div className="cap-eyebrow mb-4">Services</div>
                        <h2 className="font-serif text-4xl md:text-5xl text-navy leading-[1.05] tracking-tight">
                            Execution, not experimentation.
                        </h2>
                        <p className="mt-5 text-slate text-lg leading-relaxed">
                            Engagements are structured based on where your business is today and how quickly you need to move.
                        </p>
                    </div>

                    <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-12">
                        {SERVICES.map((s, i) => (
                            <article
                                key={s.name}
                                className="border-t border-navy pt-8 flex flex-col"
                                data-testid={`service-card-${i}`}
                            >
                                <div className="text-xs uppercase tracking-[0.22em] text-teal font-semibold">
                                    {s.label}
                                </div>
                                <h3 className="font-serif text-navy text-3xl leading-tight mt-4">
                                    {s.name}
                                </h3>
                                <p className="text-slate mt-4 leading-relaxed">{s.body}</p>
                                <Link
                                    to={s.to}
                                    className="mt-6 cap-link text-sm self-start"
                                    data-testid={`service-link-${i}`}
                                >
                                    {s.cta} <ArrowRight size={14} />
                                </Link>
                            </article>
                        ))}
                    </div>

                    <div className="mt-16">
                        <Link to="/services" className="cap-btn-secondary" data-testid="home-view-services-btn">
                            View services
                        </Link>
                    </div>
                </div>
            </section>

            {/* WHO IT'S FOR */}
            <section className="cap-section bg-navy text-offwhite" data-testid="home-who">
                <div className="cap-container grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-6">
                        <div className="cap-eyebrow mb-4" style={{ color: "#2EA6A1" }}>Who It's For</div>
                        <h2 className="font-serif text-offwhite text-4xl md:text-5xl leading-[1.05] tracking-tight">
                            Built for mid-market organisations.
                        </h2>
                        <p className="mt-6 text-offwhite/80 text-lg leading-relaxed max-w-xl">
                            This model works best for organisations with operational complexity, repeatable workflows and a need to improve capacity without immediately increasing headcount.
                        </p>
                    </div>
                    <div className="lg:col-span-6">
                        <ul className="divide-y divide-offwhite/15 border-y border-offwhite/15">
                            {WHO_POINTS.map((t, i) => (
                                <li key={t} className="flex items-start gap-5 py-5" data-testid={`who-point-${i}`}>
                                    <span className="cap-numeral text-teal text-2xl min-w-[2.5rem]">{String(i + 1).padStart(2, "0")}</span>
                                    <span className="text-offwhite text-lg md:text-xl">{t}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="cap-section" data-testid="home-final-cta">
                <div className="cap-container text-center max-w-3xl mx-auto">
                    <div className="cap-eyebrow mb-4">Start here</div>
                    <h2 className="font-serif text-navy text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                        Start with a practical conversation.
                    </h2>
                    <p className="mt-6 text-slate text-lg leading-relaxed">
                        Book a short consultation. We will identify where your business is today, what is slowing your team down, and whether a structured AI rollout makes sense.
                    </p>
                    <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                        <Link to="/contact" className="cap-btn-primary" data-testid="final-cta-primary">
                            Book a 15-minute conversation <ArrowRight size={16} />
                        </Link>
                        <Link to="/helm-framework" className="cap-btn-secondary" data-testid="final-cta-secondary">
                            Explore the Helm Framework
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}

function HelmNodeDesktop({ idx, title, copy, last }) {
    return (
        <>
            <div className="flex flex-col gap-3" data-testid={`helm-node-${idx}`}>
                <div className="text-[0.7rem] uppercase tracking-[0.24em] text-teal font-semibold">
                    {String(idx + 1).padStart(2, "0")}
                </div>
                <div className="font-serif text-navy text-3xl leading-none">{title}</div>
                <p className="text-slate text-sm leading-relaxed pr-2">{copy}</p>
            </div>
            {!last && (
                <div className="flex items-center justify-center text-navy" aria-hidden="true">
                    <span className="block h-px w-10 bg-navy" />
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="ml-1">
                        <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
            )}
        </>
    );
}
